import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import config from '../../config';

const ADMIN_TOKEN_KEY = 'skynyx_admin_token';
const ADMIN_USER_KEY = 'skynyx_admin_user';

// Create dedicated axios instance for Super Admin
const adminAxios: AxiosInstance = axios.create({
    baseURL: `${config.API_URL}/admin`,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Request interceptor to attach admin JWT token
adminAxios.interceptors.request.use((reqConfig) => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY) || sessionStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
        reqConfig.headers = reqConfig.headers || {};
        reqConfig.headers['Authorization'] = `Bearer ${token}`;
    }
    return reqConfig;
}, (error) => Promise.reject(error));

// Response interceptor to handle admin unauthenticated status
adminAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) {
            // If admin token expired or unauthorized, clear session and redirect to /admin/login
            localStorage.removeItem(ADMIN_TOKEN_KEY);
            localStorage.removeItem(ADMIN_USER_KEY);
            sessionStorage.removeItem(ADMIN_TOKEN_KEY);
            sessionStorage.removeItem(ADMIN_USER_KEY);
            if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
                window.location.href = '/admin/login';
            }
        }
        const message = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Request failed';
        return Promise.reject(new Error(message));
    }
);

export const adminAuth = {
    getToken: (): string | null => {
        return localStorage.getItem(ADMIN_TOKEN_KEY) || sessionStorage.getItem(ADMIN_TOKEN_KEY);
    },
    getUser: (): any | null => {
        const u = localStorage.getItem(ADMIN_USER_KEY) || sessionStorage.getItem(ADMIN_USER_KEY);
        try {
            return u ? JSON.parse(u) : null;
        } catch {
            return null;
        }
    },
    isAuthenticated: (): boolean => {
        const token = adminAuth.getToken();
        return !!token;
    },
    setSession: (token: string, user: any, remember = true) => {
        const storage = remember ? localStorage : sessionStorage;
        storage.setItem(ADMIN_TOKEN_KEY, token);
        storage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
    },
    clearSession: () => {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
        sessionStorage.removeItem(ADMIN_USER_KEY);
        try {
            if (typeof document !== 'undefined' && document.cookie) {
                const cookies = document.cookie.split(';');
                const hostname = window.location.hostname;
                cookies.forEach((cookie) => {
                    const name = cookie.split('=')[0]?.trim();
                    if (name) {
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${hostname};`;
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${hostname};`;
                    }
                });
            }
        } catch (e) {}
    }
};

export const adminApi = {
    // Raw HTTP methods
    get: (url: string, params?: any) => adminAxios.get(url, { params }).then((r) => r.data),
    post: (url: string, data?: any) => adminAxios.post(url, data).then((r) => r.data),
    put: (url: string, data?: any) => adminAxios.put(url, data).then((r) => r.data),
    delete: (url: string) => adminAxios.delete(url).then((r) => r.data),

    // Auth
    login: (credentials: { email: string; password: string }) => adminAxios.post('/login', credentials).then((r) => r.data),
    me: () => adminAxios.get('/me').then((r) => r.data),
    logout: () => adminAxios.post('/logout').then((r) => r.data),
    updateProfile: (data: any) => adminAxios.post('/profile', data).then((r) => r.data),
    changePassword: (data: any) => adminAxios.post('/change-password', data).then((r) => r.data),

    // Dashboard
    getStats: () => adminAxios.get('/dashboard/stats').then((r) => r.data),
    getCharts: () => adminAxios.get('/dashboard/charts').then((r) => r.data),

    // Customers: Users
    getUsers: (params?: any) => adminAxios.get('/customers/users', { params }).then((r) => r.data),
    getUser: (id: number | string) => adminAxios.get(`/customers/users/${id}`).then((r) => r.data),
    updateUser: (id: number | string, data: any) => adminAxios.put(`/customers/users/${id}`, data).then((r) => r.data),
    suspendUser: (id: number | string) => adminAxios.post(`/customers/users/${id}/suspend`).then((r) => r.data),
    activateUser: (id: number | string) => adminAxios.post(`/customers/users/${id}/activate`).then((r) => r.data),
    resetPassword: (id: number | string, password: string) => adminAxios.post(`/customers/users/${id}/reset-password`, { password }).then((r) => r.data),
    loginAsUser: (id: number | string) => adminAxios.post(`/customers/users/${id}/login-as`).then((r) => r.data),

    // Customers: Workspaces
    getWorkspaces: (params?: any) => adminAxios.get('/customers/workspaces', { params }).then((r) => r.data),
    getWorkspace: (id: number | string) => adminAxios.get(`/customers/workspaces/${id}`).then((r) => r.data),
    changeWorkspaceOwner: (id: number | string, new_owner_id: number) => adminAxios.post(`/customers/workspaces/${id}/change-owner`, { new_owner_id }).then((r) => r.data),
    suspendWorkspace: (id: number | string) => adminAxios.post(`/customers/workspaces/${id}/suspend`).then((r) => r.data),
    activateWorkspace: (id: number | string) => adminAxios.post(`/customers/workspaces/${id}/activate`).then((r) => r.data),

    // Subscriptions
    getPlans: () => adminAxios.get('/subscriptions/plans').then((r) => r.data),
    createPlan: (data: any) => adminAxios.post('/subscriptions/plans', data).then((r) => r.data),
    updatePlan: (id: number | string, data: any) => adminAxios.put(`/subscriptions/plans/${id}`, data).then((r) => r.data),
    deletePlan: (id: number | string) => adminAxios.delete(`/subscriptions/plans/${id}`).then((r) => r.data),
    assignPlan: (data: any) => adminAxios.post('/subscriptions/assign-plan', data).then((r) => r.data),
    getUsage: (params?: any) => adminAxios.get('/subscriptions/usage', { params }).then((r) => r.data),
    adjustCredits: (data: any) => adminAxios.post('/subscriptions/adjust-credits', data).then((r) => r.data),

    // WhatsApp
    getWhatsAppAccounts: (params?: any) => adminAxios.get('/whatsapp/accounts', { params }).then((r) => r.data),
    checkWhatsAppStatus: (id: number | string) => adminAxios.get(`/whatsapp/accounts/${id}/status`).then((r) => r.data),
    syncTemplates: (id: number | string) => adminAxios.post(`/whatsapp/accounts/${id}/sync-templates`).then((r) => r.data),
    disconnectWhatsApp: (id: number | string) => adminAxios.post(`/whatsapp/accounts/${id}/disconnect`).then((r) => r.data),
    getMessageLogs: (params?: any) => adminAxios.get('/whatsapp/message-logs', { params }).then((r) => r.data),
    retryMessage: (id: number | string) => adminAxios.post(`/whatsapp/message-logs/${id}/retry`).then((r) => r.data),

    // AI
    getAIUsage: () => adminAxios.get('/ai/usage').then((r) => r.data),
    getAIProviders: () => adminAxios.get('/ai/providers').then((r) => r.data),

    // MCP
    getMCPServers: (params?: any) => adminAxios.get('/mcp/servers', { params }).then((r) => r.data),
    addMCPServer: (data: any) => adminAxios.post('/mcp/servers', data).then((r) => r.data),
    editMCPServer: (id: number | string, data: any) => adminAxios.put(`/mcp/servers/${id}`, data).then((r) => r.data),
    disableMCPServer: (id: number | string) => adminAxios.post(`/mcp/servers/${id}/disable`).then((r) => r.data),
    checkMCPHealth: (id: number | string) => adminAxios.get(`/mcp/servers/${id}/health`).then((r) => r.data),
    getMCPTools: (params?: any) => adminAxios.get('/mcp/tools', { params }).then((r) => r.data),
    getMCPApps: (params?: any) => adminAxios.get('/mcp/apps', { params }).then((r) => r.data),
    getMCPExecutions: (params?: any) => adminAxios.get('/mcp/executions', { params }).then((r) => r.data),
    retryMCPExecution: (id: number | string) => adminAxios.post(`/mcp/executions/${id}/retry`).then((r) => r.data),

    // Automation
    getAutomations: (params?: any) => adminAxios.get('/automation/list', { params }).then((r) => r.data),
    enableAutomation: (id: number | string) => adminAxios.post(`/automation/${id}/enable`).then((r) => r.data),
    disableAutomation: (id: number | string) => adminAxios.post(`/automation/${id}/disable`).then((r) => r.data),
    cloneAutomation: (id: number | string) => adminAxios.post(`/automation/${id}/clone`).then((r) => r.data),
    deleteAutomation: (id: number | string) => adminAxios.delete(`/automation/${id}`).then((r) => r.data),
    getAutomationExecutions: (params?: any) => adminAxios.get('/automation/executions', { params }).then((r) => r.data),
    retryAutomationExecution: (id: number | string) => adminAxios.post(`/automation/executions/${id}/retry`).then((r) => r.data),

    // Settings
    getRoles: () => adminAxios.get('/settings/roles').then((r) => r.data),
    createRole: (data: any) => adminAxios.post('/settings/roles', data).then((r) => r.data),
    updateRole: (id: number | string, data: any) => adminAxios.put(`/settings/roles/${id}`, data).then((r) => r.data),
    getPermissions: () => adminAxios.get('/settings/permissions').then((r) => r.data),
    getAuditLogs: (params?: any) => adminAxios.get('/settings/audit-logs', { params }).then((r) => r.data),
    getSystemSettings: () => adminAxios.get('/settings/system').then((r) => r.data),
    getAiSettings: () => adminAxios.get('/settings/ai').then((r) => r.data),
    updateAiSettings: (data: { openrouter_key?: string; openrouter_model?: string }) =>
        adminAxios.post('/settings/ai', data).then((r) => r.data),
};
